import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import ViewOnlyBanner from "@/components/ViewOnlyBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import {
  Plus,
  Pencil,
  Trash2,
  Users,
  Upload,
  ImageIcon,
  Star,
  StarOff,
  ExternalLink,
  Building2,
  User,
  UserPlus,
  Mail,
  Phone,
  Briefcase,
  MapPin,
  FileText,
} from "lucide-react";
import { imageSrc } from "@/pages/Products";
import { useConfirm } from "@/context/ConfirmContext";
import { useCanEdit } from "@/lib/permissions";

function emptyContact() {
  return {
    id: Math.random().toString(36).slice(2),
    full_name: "",
    position: "",
    phone: "",
    email: "",
  };
}

function makeEmpty() {
  const c = emptyContact();
  return {
    // Default client shape is a company; the "New Individual" flow flips
    // this to "individual" and the form re-lays out to hide ABN/website/
    // logo/contact-list and treat the first contact as the person.
    client_type: "company",
    company_name: "",
    abn: "",
    website: "",
    logo_path: null,
    address: "",
    notes: "",
    contacts: [c],
    primary_contact_id: c.id,
  };
}

function makeEmptyIndividual() {
  const c = emptyContact();
  return {
    client_type: "individual",
    company_name: "",
    abn: "",
    website: "",
    logo_path: null,
    address: "",
    notes: "",
    contacts: [c],
    primary_contact_id: c.id,
  };
}

export default function Clients() {
  const canEdit = useCanEdit();
  const confirm = useConfirm();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailClient, setDetailClient] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(makeEmpty());
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients");
      setList(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openDetail = (c) => {
    setDetailClient(c);
    setDetailOpen(true);
  };

  const openNew = () => {
    setEditing(null);
    setForm(makeEmpty());
    setOpen(true);
  };
  const openNewIndividual = () => {
    setEditing(null);
    setForm(makeEmptyIndividual());
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    const contacts = c.contacts?.length ? c.contacts.map((x) => ({ ...x })) : [emptyContact()];
    setForm({
      // Legacy clients pre-date the field — treat as companies so their
      // saved layout is unchanged when re-opened.
      client_type: c.client_type === "individual" ? "individual" : "company",
      company_name: c.company_name || "",
      abn: c.abn || "",
      website: c.website || "",
      logo_path: c.logo_path || null,
      address: c.address || "",
      notes: c.notes || "",
      contacts,
      primary_contact_id: c.primary_contact_id || contacts[0].id,
    });
    setOpen(true);
  };

  const onPickLogo = () => fileInputRef.current?.click();

  const onUploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, logo_path: data.path }));
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const updateContact = (idx, patch) => {
    setForm((f) => {
      const next = [...f.contacts];
      next[idx] = { ...next[idx], ...patch };
      return { ...f, contacts: next };
    });
  };
  const addContact = () => {
    setForm((f) => {
      const c = emptyContact();
      const next = [...f.contacts, c];
      return { ...f, contacts: next, primary_contact_id: f.primary_contact_id || c.id };
    });
  };
  const removeContact = (id) => {
    setForm((f) => {
      const next = f.contacts.filter((c) => c.id !== id);
      const newPrimary =
        f.primary_contact_id === id ? next[0]?.id || null : f.primary_contact_id;
      return { ...f, contacts: next, primary_contact_id: newPrimary };
    });
  };
  const setPrimary = (id) => setForm((f) => ({ ...f, primary_contact_id: id }));

  const save = async () => {
    const isIndividual = form.client_type === "individual";
    if (isIndividual) {
      // For individuals the "name" of the client IS the primary contact's
      // full_name. Validate that and mirror it into company_name so
      // downstream lookups that key on client.company_name (quote
      // rendering, vessel client picker, etc.) keep working unchanged.
      const primary =
        form.contacts.find((c) => c.id === form.primary_contact_id) ||
        form.contacts[0];
      if (!primary?.full_name?.trim()) {
        toast.error("Full name is required");
        return;
      }
    } else if (!form.company_name.trim()) {
      toast.error("Company name is required");
      return;
    }
    try {
      const primary =
        form.contacts.find((c) => c.id === form.primary_contact_id) ||
        form.contacts[0];
      const payload = {
        ...form,
        // Sync so the individual's full name lives in company_name too
        // (see comment above). Also strip company-only fields so a
        // toggle from company→individual doesn't leak stale data.
        company_name: isIndividual
          ? primary?.full_name?.trim() || ""
          : form.company_name,
        abn: isIndividual ? "" : form.abn,
        website: isIndividual ? "" : form.website,
        logo_path: isIndividual ? null : form.logo_path,
        contacts: form.contacts.filter(
          (c) => c.full_name.trim() || c.email.trim() || c.phone.trim() || c.position.trim(),
        ),
      };
      if (!payload.contacts.length) payload.contacts = [emptyContact()];
      if (!payload.contacts.find((c) => c.id === payload.primary_contact_id)) {
        payload.primary_contact_id = payload.contacts[0].id;
      }
      if (editing) await api.put(`/clients/${editing.id}`, payload);
      else await api.post("/clients", payload);
      toast.success(editing ? "Client updated" : "Client created");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (c) => {
    const ok = await confirm({
      title: `Delete ${c.company_name}?`,
      description: "This will permanently remove the client and their contacts.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/clients/${c.id}`);
      toast.success("Client deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="p-6 sm:p-8 lg:p-10 max-w-[1400px] mx-auto pb-24">
      <ViewOnlyBanner />
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#0088CC] mb-2">CRM</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#011836]">
            Clients
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Customer companies and their contacts.
          </p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button
              onClick={openNewIndividual}
              variant="outline"
              className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5] hover:text-[#011836]"
              data-testid="new-individual-btn"
            >
              <User className="h-4 w-4 mr-2" /> New Individual
            </Button>
            <Button
              onClick={openNew}
              className="bg-[#0088CC] hover:bg-[#006DA6] text-white"
              data-testid="new-client-btn"
            >
              <Plus className="h-4 w-4 mr-2" /> New Company
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading && (
          <div className="text-[#5B6B7C] col-span-full text-sm">Loading…</div>
        )}
        {!loading && list.length === 0 && (
          <div className="sos-surface rounded-lg col-span-full p-16 text-center">
            <Users className="h-10 w-10 text-[#B6C2CD] mx-auto mb-4" />
            <div className="text-sm text-[#5B6B7C]">No clients yet</div>
          </div>
        )}
        {list.map((c) => {
          const pc = c.primary_contact || {};
          const isIndividual = c.client_type === "individual";
          const additionalContacts = (c.contacts || []).filter(
            (x) => x.id !== c.primary_contact_id,
          );
          return (
            <div
              key={c.id}
              className="sos-surface sos-surface-hover rounded-lg p-5 flex flex-col"
              data-testid={`client-card-${c.id}`}
            >
              <div className="flex items-start gap-3">
                <button
                  type="button"
                  onClick={() => openDetail(c)}
                  className="h-14 w-14 shrink-0 rounded bg-white border border-[#DDE5EC] overflow-hidden flex items-center justify-center relative hover:border-[#0088CC] transition-colors"
                  aria-label={`Open ${c.company_name}`}
                  data-testid={`open-client-logo-${c.id}`}
                >
                  {c.logo_path && !isIndividual ? (
                    <img
                      src={imageSrc(c.logo_path)}
                      alt={c.company_name}
                      className="absolute inset-0 w-full h-full object-contain p-1"
                    />
                  ) : isIndividual ? (
                    <User className="h-6 w-6 text-[#B6C2CD]" />
                  ) : (
                    <Building2 className="h-6 w-6 text-[#B6C2CD]" />
                  )}
                </button>
                <div className="min-w-0 flex-1">
                  <button
                    type="button"
                    onClick={() => openDetail(c)}
                    className="font-display text-base font-semibold text-[#011836] truncate hover:text-[#0088CC] transition-colors text-left"
                    data-testid={`open-client-name-${c.id}`}
                  >
                    {c.company_name}
                  </button>
                  <div className="text-[10px] uppercase tracking-wider text-[#5B6B7C] mt-0.5 flex items-center gap-1.5">
                    <span
                      className={`inline-flex items-center gap-1 px-1.5 py-[1px] rounded border ${
                        isIndividual
                          ? "border-[#0088CC]/40 text-[#0088CC] bg-[#E6F2FA]/40"
                          : "border-[#C4D0DB] text-[#5B6B7C] bg-white"
                      }`}
                      data-testid={`client-type-badge-${c.id}`}
                    >
                      {isIndividual ? (
                        <><User className="h-2.5 w-2.5" /> Individual</>
                      ) : (
                        <><Building2 className="h-2.5 w-2.5" /> Company</>
                      )}
                    </span>
                    {!isIndividual && (c.abn ? <span>ABN {c.abn}</span> : <span>—</span>)}
                  </div>
                  {c.website && !isIndividual && (
                    <a
                      href={c.website.startsWith("http") ? c.website : `https://${c.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 text-[11px] text-[#0088CC] hover:underline mt-1"
                    >
                      {c.website} <ExternalLink className="h-2.5 w-2.5" />
                    </a>
                  )}
                </div>
                <div className="flex gap-1">
                  {canEdit && (
                    <>
                      <button
                        onClick={() => openEdit(c)}
                        className="p-1.5 rounded text-[#5B6B7C] hover:text-[#011836] hover:bg-[#EDF1F5]"
                        data-testid={`edit-client-${c.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => remove(c)}
                        className="p-1.5 rounded text-[#5B6B7C] hover:text-[#EF4444] hover:bg-[#FDECEC]"
                        data-testid={`delete-client-${c.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-[#DDE5EC] text-xs">
                <div className="text-[10px] uppercase tracking-wider text-[#5B6B7C] flex items-center gap-1.5">
                  <Star className="h-3 w-3 text-[#0088CC] fill-[#0088CC]" />
                  Primary contact
                </div>
                {pc.full_name || pc.email ? (
                  <div className="mt-1.5 space-y-0.5">
                    <div className="text-[#011836] font-display font-semibold">{pc.full_name || "—"}</div>
                    {pc.position && <div className="text-[#5B6B7C]">{pc.position}</div>}
                    {pc.phone && <div className="text-[#5B6B7C]">{pc.phone}</div>}
                    {pc.email && <div className="text-[#0088CC] truncate">{pc.email}</div>}
                  </div>
                ) : (
                  <div className="text-[#5B6B7C] mt-1.5">No contact set</div>
                )}
                {additionalContacts.length > 0 && (
                  <HoverCard openDelay={120}>
                    <HoverCardTrigger asChild>
                      <button
                        type="button"
                        className="mt-3 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#5B6B7C] hover:text-[#011836] transition-colors"
                        data-testid={`additional-contacts-${c.id}`}
                      >
                        <UserPlus className="h-3.5 w-3.5 text-[#0088CC]" />
                        {additionalContacts.length} additional contact
                        {additionalContacts.length === 1 ? "" : "s"}
                      </button>
                    </HoverCardTrigger>
                    <HoverCardContent
                      align="start"
                      className="w-72 bg-white border border-[#DDE5EC] text-[#011836] p-3"
                    >
                      <div className="text-[10px] uppercase tracking-[0.25em] text-[#0088CC] mb-2">
                        Other contacts
                      </div>
                      <div className="space-y-3">
                        {additionalContacts.map((ac) => (
                          <div key={ac.id} className="text-xs">
                            <div className="font-display font-semibold text-[#011836]">
                              {ac.full_name || "—"}
                            </div>
                            {ac.position && (
                              <div className="text-[#5B6B7C]">{ac.position}</div>
                            )}
                            {ac.phone && (
                              <div className="text-[#5B6B7C]">{ac.phone}</div>
                            )}
                            {ac.email && (
                              <div className="text-[#0088CC] truncate">{ac.email}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent
          className="bg-white border-[#DDE5EC] text-[#011836] max-w-2xl max-h-[90vh] overflow-y-auto"
          data-testid="client-detail-dialog"
        >
          {detailClient && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl flex items-center gap-3">
                  <span className="h-12 w-12 shrink-0 rounded bg-white border border-[#DDE5EC] overflow-hidden flex items-center justify-center relative">
                    {detailClient.logo_path && detailClient.client_type !== "individual" ? (
                      <img
                        src={imageSrc(detailClient.logo_path)}
                        alt={detailClient.company_name}
                        className="absolute inset-0 w-full h-full object-contain p-1"
                      />
                    ) : detailClient.client_type === "individual" ? (
                      <User className="h-5 w-5 text-[#B6C2CD]" />
                    ) : (
                      <Building2 className="h-5 w-5 text-[#B6C2CD]" />
                    )}
                  </span>
                  <span className="text-[#011836] flex-1">{detailClient.company_name}</span>
                  <span
                    className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${
                      detailClient.client_type === "individual"
                        ? "border-[#0088CC]/40 text-[#0088CC] bg-[#E6F2FA]/40"
                        : "border-[#C4D0DB] text-[#5B6B7C] bg-white"
                    }`}
                  >
                    {detailClient.client_type === "individual" ? "Individual" : "Company"}
                  </span>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-5">
                {detailClient.client_type !== "individual" && (
                  <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#5B6B7C]">ABN</div>
                    <div className="text-[#011836] font-display mt-1">
                      {detailClient.abn || "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#5B6B7C]">
                      Website
                    </div>
                    <div className="mt-1">
                      {detailClient.website ? (
                        <a
                          href={
                            detailClient.website.startsWith("http")
                              ? detailClient.website
                              : `https://${detailClient.website}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#0088CC] hover:underline inline-flex items-center gap-1"
                        >
                          {detailClient.website} <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        <span className="text-[#5B6B7C]">—</span>
                      )}
                    </div>
                  </div>
                </div>
                )}

                {detailClient.address && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#5B6B7C] flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> Address
                    </div>
                    <div className="text-[#011836] text-sm mt-1 whitespace-pre-line">
                      {detailClient.address}
                    </div>
                  </div>
                )}

                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#0088CC] mb-2 flex items-center gap-1.5">
                    <Users className="h-3 w-3" /> Contacts
                  </div>
                  <div className="space-y-2">
                    {(detailClient.contacts || []).map((ac) => {
                      const isPrimary = ac.id === detailClient.primary_contact_id;
                      return (
                        <div
                          key={ac.id}
                          className={`rounded-md border p-3 text-sm ${
                            isPrimary
                              ? "border-[#0088CC]/50 bg-[#E6F2FA]/40"
                              : "border-[#DDE5EC] bg-white"
                          }`}
                          data-testid={`detail-contact-${ac.id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="text-[#011836] font-display font-semibold">
                                {ac.full_name || "—"}
                              </div>
                              {ac.position && (
                                <div className="text-[#5B6B7C] text-xs inline-flex items-center gap-1 mt-0.5">
                                  <Briefcase className="h-3 w-3" /> {ac.position}
                                </div>
                              )}
                            </div>
                            {isPrimary && (
                              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#0088CC]">
                                <Star className="h-3 w-3 fill-[#0088CC]" /> Primary
                              </span>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                            {ac.phone && (
                              <a
                                href={`tel:${ac.phone}`}
                                className="text-[#5B6B7C] hover:text-[#011836] inline-flex items-center gap-1.5"
                              >
                                <Phone className="h-3 w-3" /> {ac.phone}
                              </a>
                            )}
                            {ac.email && (
                              <a
                                href={`mailto:${ac.email}`}
                                className="text-[#0088CC] hover:underline inline-flex items-center gap-1.5 truncate"
                              >
                                <Mail className="h-3 w-3 shrink-0" />
                                <span className="truncate">{ac.email}</span>
                              </a>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {(!detailClient.contacts || detailClient.contacts.length === 0) && (
                      <div className="text-[#5B6B7C] text-sm">No contacts on file</div>
                    )}
                  </div>
                </div>

                {detailClient.notes && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#5B6B7C] flex items-center gap-1">
                      <FileText className="h-3 w-3" /> Notes
                    </div>
                    <div className="text-[#011836] text-sm mt-1 whitespace-pre-line">
                      {detailClient.notes}
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter className="gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDetailOpen(false)}
                  className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5]"
                  data-testid="close-detail-btn"
                >
                  Close
                </Button>
                {canEdit && (
                  <Button
                    onClick={() => {
                      setDetailOpen(false);
                      openEdit(detailClient);
                    }}
                    className="bg-[#0088CC] hover:bg-[#006DA6] text-white"
                    data-testid="edit-from-detail-btn"
                  >
                    <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-white border-[#DDE5EC] text-[#011836] max-w-2xl max-h-[90vh] overflow-y-auto"
          data-testid="client-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {form.client_type === "individual" ? (
                <User className="inline-block h-4 w-4 mr-2 text-[#0088CC]" />
              ) : (
                <Building2 className="inline-block h-4 w-4 mr-2 text-[#0088CC]" />
              )}
              {editing
                ? form.client_type === "individual"
                  ? "Edit individual"
                  : "Edit company"
                : form.client_type === "individual"
                ? "New individual"
                : "New company"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            {form.client_type === "individual" ? (
              // Individual (private) client — no company/ABN/website/logo.
              // The primary contact IS the client, so we surface just the
              // person's details plus address + notes.
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2">
                    <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                      Full name
                    </Label>
                    <Input
                      value={form.contacts[0]?.full_name || ""}
                      onChange={(e) =>
                        updateContact(0, { full_name: e.target.value })
                      }
                      placeholder="Jane Doe"
                      className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                      data-testid="individual-full-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                      Phone
                    </Label>
                    <Input
                      value={form.contacts[0]?.phone || ""}
                      onChange={(e) => updateContact(0, { phone: e.target.value })}
                      className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                      data-testid="individual-phone-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                      Email
                    </Label>
                    <Input
                      type="email"
                      value={form.contacts[0]?.email || ""}
                      onChange={(e) => updateContact(0, { email: e.target.value })}
                      className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                      data-testid="individual-email-input"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                      Address
                    </Label>
                    <Textarea
                      value={form.address}
                      rows={2}
                      onChange={(e) => setForm({ ...form, address: e.target.value })}
                      className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                      data-testid="individual-address-input"
                    />
                  </div>
                </div>
              </>
            ) : (
              <>
            {/* Logo + Company */}
            <div className="flex items-start gap-4">
              <div className="w-24 h-24 rounded border border-[#DDE5EC] bg-white overflow-hidden flex items-center justify-center relative shrink-0">
                {form.logo_path ? (
                  <img
                    src={imageSrc(form.logo_path)}
                    alt="Logo"
                    className="absolute inset-0 w-full h-full object-contain p-1"
                  />
                ) : (
                  <Building2 className="h-8 w-8 text-[#B6C2CD]" />
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={onUploadLogo}
                  data-testid="client-logo-input"
                />
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={onPickLogo}
                    disabled={uploading}
                    className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5] hover:text-[#011836]"
                    data-testid="upload-logo-btn"
                  >
                    <Upload className="h-3.5 w-3.5 mr-1.5" />
                    {uploading ? "Uploading…" : form.logo_path ? "Replace logo" : "Upload logo"}
                  </Button>
                  {form.logo_path && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setForm({ ...form, logo_path: null })}
                      className="border-[#C4D0DB] text-[#5B6B7C] hover:text-[#EF4444] hover:bg-[#FDECEC] hover:border-[#EF4444]"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
                <div className="text-[10px] text-[#5B6B7C]">PNG / JPG / WEBP · up to 8MB</div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                  Company Name
                </Label>
                <Input
                  value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                  data-testid="client-company-name-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">ABN</Label>
                <Input
                  value={form.abn}
                  onChange={(e) => setForm({ ...form, abn: e.target.value })}
                  className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                  data-testid="client-abn-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Website</Label>
                <Input
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  placeholder="https://…"
                  className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                  data-testid="client-website-input"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Address</Label>
                <Textarea
                  value={form.address}
                  rows={2}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                />
              </div>
            </div>

            {/* Contacts */}
            <div className="border-t border-[#DDE5EC] pt-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-[0.25em] text-[#0088CC]">Contacts</div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={addContact}
                  className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5] hover:text-[#011836]"
                  data-testid="add-contact-btn"
                >
                  <Plus className="h-3.5 w-3.5 mr-1.5" /> Add contact
                </Button>
              </div>

              <div className="space-y-3">
                {form.contacts.map((c, idx) => {
                  const isPrimary = c.id === form.primary_contact_id;
                  return (
                    <div
                      key={c.id}
                      className={`rounded-md border p-3 space-y-3 ${
                        isPrimary
                          ? "border-[#0088CC]/50 bg-[#E6F2FA]/40"
                          : "border-[#DDE5EC] bg-white"
                      }`}
                      data-testid={`contact-row-${idx}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {isPrimary ? (
                            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#0088CC]">
                              <Star className="h-3 w-3 fill-[#0088CC]" /> Primary
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setPrimary(c.id)}
                              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#5B6B7C] hover:text-[#0088CC]"
                              data-testid={`set-primary-${idx}`}
                            >
                              <StarOff className="h-3 w-3" /> Set as primary
                            </button>
                          )}
                        </div>
                        {form.contacts.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeContact(c.id)}
                            className="p-1 rounded text-[#5B6B7C] hover:text-[#EF4444] hover:bg-[#FDECEC]"
                            aria-label="Remove contact"
                            data-testid={`remove-contact-${idx}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-[#5B6B7C]">
                            Full name
                          </Label>
                          <Input
                            value={c.full_name}
                            onChange={(e) => updateContact(idx, { full_name: e.target.value })}
                            className="mt-1 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC] h-9"
                            data-testid={`contact-name-${idx}`}
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-[#5B6B7C]">
                            Position
                          </Label>
                          <Input
                            value={c.position}
                            onChange={(e) => updateContact(idx, { position: e.target.value })}
                            placeholder="Project Manager, Owner…"
                            className="mt-1 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC] h-9"
                            data-testid={`contact-position-${idx}`}
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-[#5B6B7C]">
                            Contact number
                          </Label>
                          <Input
                            value={c.phone}
                            onChange={(e) => updateContact(idx, { phone: e.target.value })}
                            className="mt-1 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC] h-9"
                            data-testid={`contact-phone-${idx}`}
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-[#5B6B7C]">
                            Email
                          </Label>
                          <Input
                            type="email"
                            value={c.email}
                            onChange={(e) => updateContact(idx, { email: e.target.value })}
                            className="mt-1 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC] h-9"
                            data-testid={`contact-email-${idx}`}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
              </>
            )}

            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Notes</Label>
              <Textarea
                value={form.notes}
                rows={2}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5]"
            >
              Cancel
            </Button>
            <Button
              onClick={save}
              className="bg-[#0088CC] hover:bg-[#006DA6] text-white"
              data-testid="save-client-btn"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
